/*
  <!-- Approved to ship by Jordan 2026-08-25 (iterate later). NOT LAWYER REVIEWED -->

  Rewritten 2026-08-25 in response to the legal + content review
  (docs/business/legal-content-review-2026-08-25.md, findings P1-2, P1-3, P2-3).

  STATUS: Live since 2026-08-25 (Jordan: ship now, iterate later). Not lawyer-reviewed. Every disclosure below was written against
  what the code actually does today. If any of these change, this page changes:
    - Firebase Auth/Firestore (account + quiz activity)
    - Stripe (payments; we never see card numbers)
    - SendGrid (email delivery + open/click events)
    - Google Analytics 4, Microsoft Clarity (project y1j2fivex4, src/utils/clarity.ts)
    - Meta pixel (src/utils/metaPixel.ts) + server-side CAPI (backend metaCapi.ts),
      both of which send a SHA-256 hash of the email address and nothing else.
  The single open question for a lawyer: whether the CPRA opt-out below is
  required at our volume, or whether the small-business exemption applies.
*/
import React from 'react';
import { Helmet } from 'react-helmet';
import { appHref } from '@/utils/appHref';
import { APP_NAME } from '@/pages/CaseworkApp';

const LAST_UPDATED = 'October 13, 2026';
const SUPPORT_EMAIL = 'support@socialworktestprepacademy.com';

const Section: React.FC<{ id?: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <section id={id} className="mb-6 scroll-mt-24">
    <h2 className="text-xl font-semibold mb-3 text-neutral-900">{title}</h2>
    {children}
  </section>
);

const SupportLink: React.FC = () => (
  <a className="text-primary-600 hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
);

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy - Social Work Test Prep Academy</title>
        <meta
          name="description"
          content="What Social Work Test Prep Academy collects, who we share it with, how long we keep it, and how to delete your data or opt out of ad measurement."
        />
        <meta property="og:title" content="Privacy Policy - Social Work Test Prep Academy" />
        <meta
          property="og:description"
          content="What we collect, who we share it with, how long we keep it, and how to delete your data."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.socialworktestprepacademy.com/privacy-policy" />
        <meta property="og:image" content="https://www.socialworktestprepacademy.com/images/default-og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Social Work Test Prep Academy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy - Social Work Test Prep Academy" />
        <meta name="twitter:description" content="What we collect, who we share it with, and how to delete your data." />
        <meta name="twitter:image" content="https://www.socialworktestprepacademy.com/images/default-og-image.png" />
        {/* Clean canonical — NOT window.location.href, which folds UTM/ref into
            the canonical and self-competes every campaign link (SEO audit).
            /privacy is an alias route; both point at this canonical. */}
        <link rel="canonical" href="https://www.socialworktestprepacademy.com/privacy-policy" />
      </Helmet>
      <div className="new-home-container">
        <div className="new-home-content">
          <div className="new-home-inner">
            <div className="privacy-policy">
              <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-center text-neutral-900">
                Privacy Policy
              </h1>
              <p className="text-base sm:text-lg mb-6 text-center text-neutral-600">
                Last updated: {LAST_UPDATED}
              </p>

              <Section title="1. Introduction">
                <p className="mb-4 text-neutral-700">
                  Social Work Test Prep Academy (&quot;SWTPA,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;)
                  helps people prepare for social work licensing exams. This policy explains what we collect when you
                  use our website at socialworktestprepacademy.com or our iOS and Android apps, why we collect it, who
                  we share it with, and what you can ask us to do with it. We wrote it to describe what our software
                  actually does, not what a template says.
                </p>
                <p className="mb-4 text-neutral-700">
                  Our separate on-device app for social workers has its own policy:{' '}
                  <a className="text-primary-600 hover:underline" href={appHref('/apps/casework/privacy')}>{APP_NAME} privacy policy</a>.
                </p>
              </Section>

              <Section title="2. What we collect">
                <p className="mb-2 text-neutral-700"><strong>Account information.</strong> When you sign up we collect:</p>
                <ul className="list-disc pl-5 mb-4 text-neutral-700">
                  <li>your name (if you give it) and email address</li>
                  <li>your password, which is handled by Google Firebase Authentication and is never visible to us</li>
                  <li>optional study details you enter, such as your exam type, exam date, and study goals</li>
                </ul>
                <p className="mb-2 text-neutral-700"><strong>Study activity.</strong> As you use the Service we store:</p>
                <ul className="list-disc pl-5 mb-4 text-neutral-700">
                  <li>the questions you answered, your answers, whether they were correct, and how long you took</li>
                  <li>quiz and practice-exam sessions, diagnostic results, flashcard reviews, and streak data</li>
                  <li>your messages to our AI features, such as Ask Pam and the clinical simulations</li>
                </ul>
                <p className="mb-2 text-neutral-700"><strong>Payment information.</strong> Purchases are processed by Stripe. Stripe collects and stores your card details; we never receive or store your card number. We receive a confirmation that a payment succeeded, the amount, and an identifier we use to grant your access.</p>
                <p className="mb-2 text-neutral-700"><strong>Email activity.</strong> Our email is sent through SendGrid, which reports back whether a message was delivered, opened, clicked, bounced, or unsubscribed. We store those events against your email address so we stop emailing people who do not want to hear from us.</p>
                <p className="mb-2 text-neutral-700"><strong>Device and usage information.</strong> Standard technical data such as browser or device type, operating system, approximate location derived from IP address, pages viewed, and referring links.</p>
              </Section>

              <Section title="3. How we use it">
                <ul className="list-disc pl-5 mb-4 text-neutral-700">
                  <li>to run the Service, including the adaptive engine that picks your next question</li>
                  <li>to show you your progress, readiness, and weak areas</li>
                  <li>to process your purchase and give you the access you paid for</li>
                  <li>to send you account, study, and marketing email you can opt out of at any time</li>
                  <li>to answer your support requests</li>
                  <li>to understand which parts of the product work and to fix the parts that do not</li>
                  <li>to measure whether our advertising is reaching the right people</li>
                  <li>to detect fraud, credential sharing, and abuse, and to meet legal obligations</li>
                </ul>
              </Section>

              <Section title="4. Analytics, session replay, and advertising measurement">
                <p className="mb-4 text-neutral-700">We use these third-party tools:</p>
                <ul className="list-disc pl-5 mb-4 text-neutral-700">
                  <li>
                    <strong>Google Analytics 4</strong> collects usage statistics such as pages visited, session
                    duration, general location, and which actions you completed. We have enabled Google signals,
                    which lets Google associate this activity with signed-in Google accounts that have Ads
                    Personalization turned on, and gives us aggregated age, gender, and interest reports. You can
                    opt out at any time through your Google account's Ads Settings or the Google Analytics opt-out
                    browser add-on.
                  </li>
                  <li>
                    <strong>Microsoft Clarity</strong> records how visitors interact with our website and apps (taps,
                    clicks, scrolling, and navigation) and produces session replays and heatmaps. On the website it
                    runs only after you choose Accept on the recording notice; if you decline or do not answer, it
                    does not load. To change your choice, clear this site&apos;s data in your browser. Text you type into
                    forms is masked and is not captured in recordings. Microsoft may use aggregated interaction data as
                    described in its own privacy documentation.
                  </li>
                  <li>
                    <strong>Meta pixel and Conversions API.</strong> If you arrive from an ad, a Meta measurement tag
                    records that the visit and related actions (such as starting the free diagnostic or purchasing)
                    occurred. When you are signed in, a one-way SHA-256 hash of your email address may be sent to Meta,
                    both from your browser and from our servers, so Meta can match the action to the ad you saw. We do
                    not send your name, your plain email address, your phone number, or your quiz answers. See section
                    8 for how to opt out.
                  </li>
                  <li>
                    <strong>Other ad platforms.</strong> Google Ads, Apple Search Ads, and Reddit measurement tags may
                    record that a conversion happened. These receive event data, not your email address.
                  </li>
                </ul>
                <p className="mb-4 text-neutral-700">
                  These tools never receive your quiz answers, your AI conversations, or your password.
                </p>
              </Section>

              <Section title="5. Cookies and similar technologies">
                <p className="mb-4 text-neutral-700">
                  We use cookies and similar browser storage for three things: keeping you signed in (strictly
                  necessary), remembering preferences, and the analytics and advertising measurement described in
                  section 4. You can block or delete cookies in your browser settings. Blocking strictly necessary
                  cookies will sign you out and break parts of the Service. We do not currently show a cookie consent
                  banner because we serve a United States audience; if that changes, we will add one.
                </p>
              </Section>

              <Section title="6. Who we share information with">
                <p className="mb-4 text-neutral-700">
                  We do not sell your personal information. We share it only with service providers who need it to run
                  the Service, and with the ad platforms described in section 4 for measurement:
                </p>
                <ul className="list-disc pl-5 mb-4 text-neutral-700">
                  <li>Google Firebase, for authentication, database, and hosting infrastructure</li>
                  <li>Stripe, for payment processing</li>
                  <li>SendGrid, for sending email and reporting delivery events</li>
                  <li>Google Analytics and Microsoft Clarity, for product analytics</li>
                  <li>Meta, Google, Apple, and Reddit, for advertising measurement</li>
                  <li>AI providers that power Ask Pam and the clinical simulations</li>
                </ul>
                <p className="mb-4 text-neutral-700">
                  We may also disclose information if the law requires it, to protect our rights or someone&apos;s
                  safety, or as part of a merger or sale of the business, in which case we will say so here first.
                </p>
              </Section>

              <Section title="7. Email choices">
                <p className="mb-4 text-neutral-700">
                  Marketing and study emails include an unsubscribe link, and you can also manage your email settings
                  in your account. Unsubscribing takes effect right away and we honor it across all of our marketing
                  sends. We will still send you essential account messages, such as receipts, password resets, and
                  security notices, because those are part of running your account.
                </p>
              </Section>

              <Section id="do-not-sell" title="8. Do Not Sell or Share My Personal Information">
                <p className="mb-4 text-neutral-700">
                  We do not sell your personal information for money, and we never have. However, as described in
                  section 4, we send a hashed version of your email address to Meta so it can measure whether its
                  advertising works. Under California&apos;s privacy law and similar state laws, that counts as
                  &quot;sharing&quot; for cross-context behavioral advertising, so we are telling you plainly and giving
                  you a way out.
                </p>
                <p className="mb-4 text-neutral-700">
                  To opt out, email <SupportLink /> with the subject line &quot;Do Not Share&quot; from the address on
                  your account. We will stop including your information in advertising measurement within 15 business
                  days and confirm when it is done. You do not need an account to ask, and asking will not change your
                  price, your access, or the service you get.
                </p>
                <p className="mb-4 text-neutral-700">
                  You can also limit this at the platform level: turn on Global Privacy Control in a supported browser,
                  use your browser&apos;s tracking protections, or adjust ad settings in your Meta account.
                </p>
              </Section>

              <Section title="9. Your rights">
                <p className="mb-4 text-neutral-700">
                  Depending on where you live, you may have the right to access the personal information we hold, to
                  correct it, to delete it, to receive a copy in a portable format, to opt out of sharing for
                  advertising, and to not be discriminated against for exercising any of these rights.
                </p>
                <p className="mb-4 text-neutral-700">
                  You can delete your account and its data yourself from account settings, or at{' '}
                  <a className="text-primary-600 hover:underline" href={appHref('/delete-account')}>socialworktestprepacademy.com/delete-account</a>.
                  For anything else, email <SupportLink />. We respond within 30 days and we may ask you to confirm
                  your identity by replying from the email address on the account.
                </p>
              </Section>

              <Section title="10. How long we keep information">
                <ul className="list-disc pl-5 mb-4 text-neutral-700">
                  <li><strong>Account and study data:</strong> while your account is open, and deleted when you delete your account.</li>
                  <li><strong>Payment records:</strong> kept for up to 7 years, because tax and accounting rules require it.</li>
                  <li><strong>Email delivery and engagement events:</strong> up to 2 years, and unsubscribe records are kept indefinitely so we can keep honoring your opt-out.</li>
                  <li><strong>Analytics and session replay:</strong> retained by Google and Microsoft under their own schedules, generally up to 14 months and 30 days respectively.</li>
                  <li><strong>Support email:</strong> up to 3 years.</li>
                </ul>
                <p className="mb-4 text-neutral-700">
                  We may keep information longer where the law requires it or to resolve a dispute.
                </p>
              </Section>

              <Section title="11. Security">
                <p className="mb-4 text-neutral-700">
                  Data is encrypted in transit and at rest on Google Cloud infrastructure, access is limited to the
                  people who need it, and payment card data never touches our servers. No system is perfectly secure,
                  so we cannot promise absolute security, but if a breach affects your information we will notify you
                  as the law requires.
                </p>
              </Section>

              <Section title="12. Children">
                <p className="mb-4 text-neutral-700">
                  The Service is built for adult professionals and is not directed at children under 13. We do not
                  knowingly collect personal information from children under 13. If you believe a child has given us
                  information, email <SupportLink /> and we will delete it.
                </p>
              </Section>

              <Section title="13. Where your information is processed">
                <p className="mb-4 text-neutral-700">
                  We operate in the United States and our service providers process information there. If you use the
                  Service from outside the United States, you understand that your information will be processed in the
                  United States, where privacy laws may differ from those in your country.
                </p>
              </Section>

              <Section title="14. Changes to this policy">
                <p className="mb-4 text-neutral-700">
                  We may update this policy. We will post the new version here and change the &quot;Last updated&quot;
                  date, and for material changes we will notify you by email.
                </p>
              </Section>

              <Section title="15. Contact us">
                <p className="mb-4 text-neutral-700">
                  Questions, requests, or complaints about privacy go to <SupportLink />. A person reads them.
                </p>
              </Section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
